<template>
  <el-dialog
    :visible.sync="dialogVisible"
    :title="order ? '确认充值' : '充值'"
    width="500px"
    custom-class="up-deposit-dialog"
    append-to-body
    :close-on-click-modal="false"
    @closed="handleClosed"
  >
    <template v-if="!order">
      <div class="deposit-field">
        <label>到账账户</label>
        <div class="deposit-account">
          <i />
          <strong>资金账户</strong>
        </div>
      </div>

      <div class="deposit-field">
        <label>充值金额（USDT）</label>
        <el-input
          :value="amount"
          inputmode="numeric"
          placeholder="请输入充值金额"
          @input="handleAmountInput"
        >
          <span slot="suffix" class="deposit-unit">USDT</span>
        </el-input>
        <div class="deposit-quick-amounts" aria-label="便捷充值">
          <button
            v-for="item in quickAmounts"
            :key="item"
            :class="{ active: Number(amount) === item }"
            type="button"
            @click="selectQuickAmount(item)"
          >
            {{ item }} USDT
          </button>
        </div>
      </div>

      <div class="deposit-field">
        <label>充值方式</label>
        <div class="deposit-desktop-method">
          <el-select
            v-model="selectedType"
            style="width: 100%"
            :loading="methodsLoading"
            :disabled="methodsLoading || !methods.length"
            placeholder="请选择充值方式"
          >
            <el-option
              v-for="item in methods"
              :key="String(item.type)"
              :label="methodLabel(item)"
              :value="item.type"
            >
              <div class="deposit-method-option">
                <img v-if="item.chain_icon" :src="item.chain_icon" alt="">
                <span>{{ methodLabel(item) }}</span>
              </div>
            </el-option>
          </el-select>
        </div>
        <div v-if="methods.length" class="deposit-mobile-methods" aria-label="充值网络">
          <button
            v-for="item in methods"
            :key="`mobile-${String(item.type)}`"
            :class="{ active: String(selectedType) === String(item.type) }"
            type="button"
            @click="selectedType = item.type"
          >
            {{ methodLabel(item) }}
          </button>
        </div>
        <div v-if="!methodsLoading && !methods.length" class="deposit-empty">
          <span>充值方式加载失败</span>
          <button type="button" @click="$emit('reload-methods')">重新加载</button>
        </div>
      </div>

      <div class="deposit-notice">
        充值订单生成后，请严格按照订单金额和对应公链完成转账。
      </div>
    </template>

    <template v-else>
      <div class="deposit-confirm-amount">
        <span>充值金额</span>
        <strong>{{ displayAmount }} USDT</strong>
      </div>

      <div class="recharge-order-address-heading">点击复制下方充值地址</div>
      <button
        class="recharge-order-address"
        type="button"
        :disabled="!resolvedAddress"
        @click="copyAddress"
      >
        <span>{{ resolvedAddress || '充值地址加载失败' }}</span>
        <i class="el-icon-document-copy" aria-hidden="true" />
      </button>

      <div class="deposit-qr">
        <img v-if="qrCodeUrl" :src="qrCodeUrl" alt="充值地址二维码">
        <div v-else class="deposit-qr__loading">
          <i :class="submitting ? 'el-icon-loading' : 'el-icon-warning-outline'" />
          <span>{{ submitting ? '二维码加载中' : '二维码加载失败' }}</span>
        </div>
      </div>

      <p v-if="order.time" class="deposit-time">
        请务必在 <strong>{{ order.time }}</strong> 之前完成付款，超时请重新生成充值订单。
      </p>

      <div class="deposit-important">
        <strong>重要提示</strong>
        <p>1. 当前 USDT 支付公链协议为 {{ resolvedMethod || '--' }}。</p>
        <p>2. <em>充值金额请务必按照上方显示金额充值</em>，否则系统无法确认充值成功；金额不一致时系统无法找回。</p>
      </div>
    </template>

    <span slot="footer" class="deposit-footer">
      <button class="up-ghost-button" type="button" @click="dialogVisible = false">
        {{ order ? '关闭' : '取消' }}
      </button>
      <button
        v-if="!order"
        class="up-primary-button"
        type="button"
        :disabled="submitting || methodsLoading || !methods.length"
        @click="submit"
      >
        {{ submitting ? '正在生成订单…' : '立即充值' }}
      </button>
      <button
        v-else
        class="up-primary-button"
        type="button"
        @click="dialogVisible = false"
      >
        我已完成充值
      </button>
    </span>
  </el-dialog>
</template>

<script>
function text(value) {
  return value === null || value === undefined ? '' : String(value)
}

export default {
  name: 'DepositModal',
  props: {
    visible: { type: Boolean, default: false },
    methods: { type: Array, default: () => [] },
    methodsLoading: { type: Boolean, default: false },
    submitting: { type: Boolean, default: false },
    order: { type: Object, default: null },
    qrCodeUrl: { type: String, default: '' }
  },
  data() {
    return {
      amount: '',
      selectedType: null,
      quickAmounts: [50, 100, 200, 500, 1000, 5000]
    }
  },
  computed: {
    dialogVisible: {
      get() { return this.visible },
      set(value) { this.$emit('update:visible', value) }
    },
    selectedMethod() {
      return this.methods.find(item => String(item.type) === String(this.selectedType)) || null
    },
    resolvedAddress() {
      const data = this.order || {}
      return text(data.address || data.chongzhi_url)
    },
    resolvedMethod() {
      const data = this.order || {}
      return text(data.method || data.chain_detail || this.methodLabel(this.selectedMethod))
    },
    displayAmount() {
      const data = this.order || {}
      return text(data.money || data.amount || this.amount || '--')
    }
  },
  watch: {
    methods(items) {
      const preferred = items.find(item => {
        const label = this.methodLabel(item).toUpperCase()
        return label.includes('TRON') && label.includes('TRC20')
      }) || items.find(item => this.methodLabel(item).toUpperCase().includes('TRC20'))
      this.selectedType = preferred ? preferred.type : null
    },
    visible(value) {
      if (value && !this.order) this.selectedType = null
    }
  },
  methods: {
    methodLabel(item) {
      if (!item) return ''
      return text(item.chain_detail || item.name || item.label || item.type)
    },
    handleAmountInput(value) {
      let next = text(value).replace(/[^\d]/g, '')
      if (next.length > 1) next = next.replace(/^0+/, '')
      this.amount = next
    },
    selectQuickAmount(amount) {
      this.amount = String(amount)
    },
    submit() {
      if (!this.amount) {
        this.$message.warning('请输入充值金额')
        return
      }
      if (!this.selectedMethod) {
        this.$message.warning('请选择充值方式')
        return
      }
      this.$emit('submit', {
        amount: Number(this.amount),
        payType: this.selectedMethod.type,
        method: this.methodLabel(this.selectedMethod)
      })
    },
    async copyAddress() {
      if (!this.resolvedAddress) return
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(this.resolvedAddress)
        } else {
          const input = document.createElement('input')
          input.value = this.resolvedAddress
          document.body.appendChild(input)
          input.select()
          document.execCommand('copy')
          document.body.removeChild(input)
        }
        this.$message.success('复制成功')
      } catch (error) {
        this.$message.error('复制失败，请手动复制充值地址')
      }
    },
    handleClosed() {
      this.amount = ''
      this.selectedType = null
      this.$emit('closed')
    }
  }
}
</script>

<style>
.up-deposit-dialog {
  max-width: calc(100vw - 28px);
  border-radius: 14px;
  overflow: hidden;
}

.up-deposit-dialog .el-dialog__header {
  padding: 22px 24px 16px;
  border-bottom: 1px solid #edf0f5;
}

.up-deposit-dialog .el-dialog__title {
  color: #1b1f2a;
  font-size: 18px;
  font-weight: 800;
}

.up-deposit-dialog .el-dialog__headerbtn {
  top: 23px;
  right: 24px;
}

.up-deposit-dialog .el-dialog__body {
  padding: 20px 24px 4px;
}

.up-deposit-dialog .el-dialog__footer {
  padding: 18px 24px 24px;
}

.deposit-field + .deposit-field {
  margin-top: 16px;
}

.deposit-field label {
  display: block;
  margin-bottom: 8px;
  color: #5a6072;
  font-size: 13.5px;
  font-weight: 600;
}

.deposit-account {
  display: flex;
  min-height: 42px;
  padding: 0 13px;
  align-items: center;
  gap: 9px;
  border: 1px solid #eef0f5;
  border-radius: 8px;
  background: #f7f8fb;
  color: #1b1f2a;
  font-size: 14px;
}

.deposit-account i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #0e9f5c;
}

.up-deposit-dialog .el-input__inner {
  height: 42px;
  padding: 0 14px;
  border-color: #dfe3ec;
  border-radius: 8px;
  background: #fafbfd;
  color: #1b1f2a;
}

.up-deposit-dialog .el-input__inner:focus {
  border-color: #1f6bff;
}

.up-deposit-dialog .el-input__suffix {
  right: 12px;
}

.deposit-unit {
  color: #8a90a0;
  font-size: 12px;
  line-height: 42px;
}

.deposit-quick-amounts {
  display: grid;
  margin-top: 10px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.deposit-quick-amounts button {
  min-width: 0;
  height: 34px;
  padding: 0 8px;
  border: 1px solid #e1e6f0;
  border-radius: 8px;
  background: #f7f9fd;
  color: #5a6072;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
}

.deposit-quick-amounts button:hover,
.deposit-quick-amounts button.active {
  border-color: #9dbdff;
  background: #eef4ff;
  color: #1f6bff;
}

.deposit-mobile-methods { display: none; }

.deposit-method-option {
  display: flex;
  height: 100%;
  align-items: center;
  gap: 8px;
}

.deposit-method-option img {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.deposit-empty {
  display: flex;
  margin-top: 8px;
  padding: 10px 12px;
  align-items: center;
  justify-content: space-between;
  border-radius: 8px;
  background: #fff2f1;
  color: #e5484d;
  font-size: 12px;
}

.deposit-empty button {
  padding: 0;
  border: 0;
  background: transparent;
  color: #1f6bff;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
}

.deposit-empty button:hover {
  color: #1558db;
}

.deposit-notice {
  margin-top: 18px;
  padding: 11px 13px;
  border-radius: 8px;
  background: #fbf1df;
  color: #91600a;
  font-size: 12.5px;
  line-height: 1.6;
}

.deposit-confirm-amount {
  display: flex;
  padding: 14px;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #e3e9f5;
  border-radius: 10px;
  background: #f7f9fd;
}

.deposit-confirm-amount span {
  color: #5a6072;
  font-size: 13px;
  font-weight: 600;
}

.deposit-confirm-amount strong {
  color: #1f6bff;
  font-size: 19px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

.recharge-order-address-heading {
  margin: 18px 0 8px;
  color: #5a6072;
  font-size: 13px;
  font-weight: 600;
}

.recharge-order-address {
  display: flex;
  width: 100%;
  min-height: 46px;
  padding: 11px 13px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: 1px dashed #b9c8ea;
  border-radius: 9px;
  background: #f6f9ff;
  color: #3a3f4e;
  cursor: pointer;
  text-align: left;
}

.recharge-order-address span {
  overflow-wrap: anywhere;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12.5px;
}

.recharge-order-address i {
  flex: 0 0 auto;
  color: #1f6bff;
  font-size: 17px;
}

.recharge-order-address:disabled {
  cursor: default;
  opacity: .7;
}

.deposit-qr {
  display: grid;
  width: 132px;
  height: 132px;
  margin: 18px auto 0;
  place-items: center;
  border: 1px solid #edf0f5;
  border-radius: 10px;
  background: #fff;
}

.deposit-qr img {
  width: 118px;
  height: 118px;
  object-fit: contain;
}

.deposit-qr__loading {
  display: grid;
  place-items: center;
  gap: 7px;
  color: #98a0af;
  font-size: 12px;
}

.deposit-qr__loading i {
  color: #1f6bff;
  font-size: 20px;
}

.deposit-time {
  margin: 10px auto 0;
  color: #5a6072;
  font-size: 12.5px;
  line-height: 1.55;
  text-align: center;
}

.deposit-time strong {
  color: #e5484d;
  font-size: 14px;
}

.deposit-important {
  margin-top: 18px;
  padding: 13px;
  border-radius: 8px;
  background: #fbf1df;
  color: #6f521c;
}

.deposit-important > strong {
  display: block;
  margin-bottom: 6px;
  color: #7b5512;
  font-size: 13px;
}

.deposit-important p {
  margin: 4px 0 0;
  font-size: 12px;
  line-height: 1.65;
}

.deposit-important em {
  color: #e5484d;
  font-style: normal;
  font-weight: 700;
}

.deposit-footer {
  display: flex;
  gap: 10px;
}

.deposit-footer button {
  min-height: 42px;
  flex: 1;
}

.deposit-footer button:disabled {
  cursor: wait;
  opacity: .6;
}
</style>
